import { AfterViewInit, Directive, DoCheck, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from "@angular/core";
import { NgModelGroup } from "@angular/forms";
import { KeyHandlerService, SecurityService } from "@tylertech/core-module";
import { FieldDensity } from "@tylertech/forge";
import { Utilities } from "@tylertech/shared-module";
import { KeyValuePair } from "@tylertech/tyler-api-types";
import { Subject, takeUntil } from "rxjs";
import { AugmentedModelObject, MetadataKey, ValueChangedParms } from "../../../../interfaces";
import { CacheablePropertyMetadata, CachedCodeLookupService, MetadataEventService, MetadataInputSelectService, MetadataPropertyService, MetadataService } from "../../../../services";
import { ArrayHelpers, ObjectHelpers } from "../../../../utilities";

export const MAX_RECORDS_RENDERED = 20;

function areEquivalent<T>(a: T, b: T): boolean
{
    if (Utilities.IsNullOrUndefined(a) && Utilities.IsNullOrUndefined(b)) return true;
    return JSON.stringify(a) === JSON.stringify(b);
}

@Directive()
export abstract class MetadataInputSelectComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy
{
    modelGroup = inject(NgModelGroup);
    private readonly $metadataService = inject(MetadataService);
    readonly $metadataInputSelectService = inject(MetadataInputSelectService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    readonly $metadataEventService = inject(MetadataEventService);
    private readonly $cachedCodeLookupService = inject(CachedCodeLookupService);
    private readonly $keyHandlerService = inject(KeyHandlerService);
    private readonly $securityService = inject(SecurityService);

    // not really readonly, but we should treat it as immutable within this component.
    @Input()
    readonly model: MetadataKey | MetadataKey[];

    @Input()
    metadataModel: AugmentedModelObject; // model should be a field on metadataModel, FYI

    @Input()
    parent: AugmentedModelObject; // metadataModel should be a field on parent, FYI

    @Input()
    propertyMetadata: CacheablePropertyMetadata;

    @Input()
    viewOnly?: boolean;

    @Input()
    dense?: boolean;

    @Input()
    forCellEdit?: boolean;

    @Output()
    onValueChanged = new EventEmitter<ValueChangedParms>();

    @Input()
    hint?: string;

    @Input()
    index?: number;

    @Input()
    compact?: boolean;

    @Input()
    error: string;

    private isInitialized: boolean;

    private _loadingPromise: Promise<any>;

    /**
     * TODO: this could be replaced with a calculated property that tests if the `!_loadingPromise`
     */
    private selectLoading: boolean = false;

    _options: KeyValuePair<MetadataKey, string>[];

    private dependentValues: any[];

    private filterValues: any[];

    private lookupPropertyValuesSignature: string;

    private constantFilter: string;

    private requiredPropertyValue: boolean;

    protected _selected: MetadataKey | MetadataKey[];

    private _optionSearch: string = "";

    loadMoreCount: number = 1;

    /** Accessible in the template */ isViewOnly: boolean;

    /**
     * Metadata's Recalculate on change functionality mutates the metadataModel in a way that will not trigger a binding change event,
     * so we need to have a way to do a simple check in `$doCheck` to determine if the `this.model` value was changed with out our notice.
     * This should only be set in `$onInit` and `onModelChanged`.
     */
    private unboundModel: MetadataKey | MetadataKey[];

    density: FieldDensity;

    protected readonly destructor$ = new Subject<void>();

    ngOnInit(): void
    {
        if (this.forCellEdit)
            this.setRecalculateStatus();
        const self = this;
        this.unboundModel = Utilities.deepCopy(this.model);
        this.isViewOnly = true;
        if (Utilities.IsNullOrUndefined(this.propertyMetadata?.LookupProperty))
        {
            this.$metadataEventService.onLookupTypeCacheClear.pipe(takeUntil(this.destructor$))
                .subscribe((type) =>
                {
                    if (type === this.propertyMetadata.Lookup)
                        self.forceLoadSelect();
                });
        }

        this.loadSelect().finally(() => this.finalizeSelect());
        this.initializeListeners();
        this.density = this.$metadataService.getInputDensity(this.compact);
    }

    ngAfterViewInit(): void
    {
        if (this.isInitialized)
            return;
        this.isInitialized = true;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    infiniteScroll(e): void
    {
        if (this.showLoadMore)
        {
            const list = e.currentTarget;
            const compareHeight = list.scrollHeight - list.offsetHeight;
            //Compare within +/-1 to handle for browser zoom
            if (Math.abs(Math.round(list.scrollTop) - compareHeight) <= 1)
            {
                this.loadMore();
            }
        }
    }

    ngDoCheck(): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        if ((this.unboundModel && this.model) || this.propertyMetadata.$$forceReload)
        {
            if (!Utilities.objectsEqual(this.unboundModel, this.model) || this.propertyMetadata.$$forceReload)
            {
                this.onModelChanged(this.model);
            }
        }
        const dependentValuesChanged = this.updateDependentValues();
        const filterValuesChanged = this.updateFilterValues();
        const lookupPropertyValuesChanged = this.updateLookupPropertyValue();
        const constantFilterValueChanged = this.updateConstantFilterValue();
        const requiredPropertyChanged = this.updateRequiredPropertyValue();
        if (
            dependentValuesChanged ||
            filterValuesChanged ||
            lookupPropertyValuesChanged ||
            constantFilterValueChanged ||
            requiredPropertyChanged
        )
        {
            if (lookupPropertyValuesChanged)
                //Force refresh of control text in case lookup values have changed
                this._selected = undefined;
            if (!this._loadingPromise)
                this.forceLoadSelect();
        }
        this.isViewOnly =
            (this.viewOnly && !this.propertyMetadata.ViewOnlyEnabled) ||
            this.$metadataPropertyService.isViewOnly(this.propertyMetadata, this.metadataModel, this.parent) ||
            this.disableForRecalculate ||
            this.selectLoading ||
            !this._options ||
            this._options.length === 0;
    }

    ngOnChanges(_changes: SimpleChanges): void
    {
        if (!this.isInitialized)
        {
            return;
        }
        const changedKeys = Object.keys(_changes);
        for (const key of changedKeys)
        {
            switch (key)
            {
                case "model":
                    this.onModelChanged(_changes[key].currentValue);
                    break;
            }
        }
    }

    disableForRecalculate: boolean = false;
    allowRecalculateSpinner: boolean = false;

    get showSpinner(): boolean
    {
        const showSpinnerValue = this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel);
        if (!showSpinnerValue)
        {
            this.disableForRecalculate = false;
            this.allowRecalculateSpinner = false;
        }
        return this.allowRecalculateSpinner && showSpinnerValue;
    }

    setRecalculateStatus(): void
    {
        if (this.$metadataPropertyService.isBeingRecalculated(this.propertyMetadata, this.metadataModel))
        {
            this.disableForRecalculate = true;
            this.allowRecalculateSpinner = true;
        }
    }

    get isInvalid(): boolean
    {
        if (this.isRequired)
        {
            const self = this;
            let isEmpty = false;
            if (Utilities.IsNullOrUndefined(this.selected))
            {
                isEmpty = true;
            }

            if (Array.isArray(this.selected))
            {
                isEmpty = ArrayHelpers.none(self.options, x => ArrayHelpers.any(<MetadataKey[]>this.selected, y => x.Key === y));
            }

            this.error = isEmpty ? `${this.Label} is Required` : "";
        }
        else
        {
            this.error = "";
        }

        return this.$metadataService.invalidInput(this.error, this.modelGroup?.control?.controls[this.propertyMetadata.Name]);
    }

    get popupId(): string
    {
        let indexChunk = "";
        if (!Utilities.IsNullOrUndefined(this.index))
            indexChunk = "-" + this.index;
        else if (this.modelGroup?.name)
            indexChunk = "-" + this.modelGroup.name.substring(this.modelGroup.name.length - 6, this.modelGroup.name.length - 1);
        return this.Name + indexChunk;
    }

    private onModelChanged(value: MetadataKey | MetadataKey[]): void
    {
        const self = this;
        if (this._loadingPromise)
        {
            this._loadingPromise.then(a =>
            {
                self.onModelChanged(value);
                return a;
            });
            return;
        }
        if (this.propertyMetadata.$$forceReload)
        {
            self._options = null;
            self.clearDeselectList();
            self.loadSelect(true)
                .then(() =>
                {
                    self.selected = value;
                    self.onModelChanged(self.model);
                });
            self.propertyMetadata.$$forceReload = false;
            return;
        }
        this.unboundModel = Utilities.deepCopy(value);
        const change = value as MetadataKey[];
        this.selected = change ? this.applySort(this._options.filter(x => change.indexOf(x.Key) > -1)).map(x => x.Key) : [];
    }

    get selected(): MetadataKey | MetadataKey[]
    {
        return this._selected;
    }

    set selected(value: MetadataKey | MetadataKey[])
    {
        this.setSelected(value);
    }

    get Name(): string
    {
        return this.propertyMetadata.Name;
    }

    get Label(): string
    {
        return this.forCellEdit
            ? ""
            : this.$metadataService.EvaluateString(this.propertyMetadata.Label, this.parent, this.metadataModel);
    }

    get isRequired(): boolean
    {
        return (
            !this.isViewOnly &&
            this.$metadataPropertyService.isRequired(this.propertyMetadata, this.metadataModel, this.parent)
        );
    }

    get options(): KeyValuePair<MetadataKey, string>[]
    {
        return this._options;
    }

    get optionSearch(): string
    {
        return this._optionSearch;
    }

    set optionSearch(value: string)
    {
        this._optionSearch = value;
    }

    private updateDependentValues(): boolean
    {
        const list = this.$metadataPropertyService.getDependentValuesForProperty(
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        );
        if (!areEquivalent(this.dependentValues, list))
        {
            this.dependentValues = list;
            return true;
        }
        return false;
    }

    private updateLookupPropertyValue(): boolean
    {
        if (!this.propertyMetadata.LookupProperty)
            return false;
        const list = this.$metadataService.DependentModuleValue(
            this.propertyMetadata.LookupProperty,
            this.parent,
            this.metadataModel
        ) ?? [];
        const signature = ObjectHelpers.stringifyCircular(list);
        if (this.lookupPropertyValuesSignature === signature)
            return false;
        this.lookupPropertyValuesSignature = signature;
        return true;
    }

    private updateConstantFilterValue(): boolean
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(this.propertyMetadata.ConstantFilter))
        {
            return false;
        }
        const currentFilter = this.$metadataService.EvaluateString(
            this.propertyMetadata.ConstantFilter,
            this.parent,
            this.metadataModel, null, true
        );
        if (!(this.constantFilter === currentFilter))
        {
            this.constantFilter = currentFilter;
            return true;
        }
        return false;
    }

    private updateRequiredPropertyValue(): boolean
    {
        if (Utilities.IsNullOrUndefinedOrEmpty(this.propertyMetadata.RequiredProperty))
        {
            return false;
        }
        const currentRequiredPropertyValue = this.$metadataPropertyService.isRequired(
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        );
        if (!(this.requiredPropertyValue === currentRequiredPropertyValue))
        {
            this.requiredPropertyValue = currentRequiredPropertyValue;
            return true;
        }
        return false;
    }

    private updateFilterValues(): boolean
    {
        const list = this.$metadataPropertyService.getFilterByValuesForProperty(
            this.propertyMetadata,
            this.metadataModel,
            this.parent
        );
        if (!areEquivalent(this.filterValues, list))
        {
            this.filterValues = list;
            return true;
        }
        return false;
    }

    private forceLoadSelect(clearCache: boolean = false): void
    {
        this._options = null;
        this.clearDeselectList();
        this.loadSelect(clearCache);
    }

    private loadSelect(clearCache: boolean = false): Promise<any>
    {
        const self = this;
        if (this._options)
        {
            return Promise.resolve();
        }
        if (this.selectLoading)
        {
            return this._loadingPromise;
        }
        this.selectLoading = true;
        this._loadingPromise = this.QueryLookupService(clearCache)
            .then(findSelected)
            .finally(() =>
            {
                this._loadingPromise = undefined;
                this.selectLoading = false;
            });
        return this._loadingPromise;
        function findSelected(result: KeyValuePair<MetadataKey, string>[]): void
        {
            // REVIEW - I haven't really thought this one through....
            const modelArray = <MetadataKey[]>self.model;
            if (!modelArray)
            {
                self.selected = [];
            }
            else if (modelArray && !self.selected)
            {
                self.selected = self.applySort(result).map(x => x.Key)
                    .filter(x => modelArray.indexOf(x) > -1);
            }
        }
    }

    private QueryLookupService(clearCache: boolean = false): Promise<KeyValuePair<MetadataKey, string>[]>
    {
        const self = this;
        const lookupType = this.propertyMetadata.Lookup;
        const filterString = this.createOdataFilter();
        if (this.propertyMetadata.LookupProperty)
        {
            return Promise.resolve(
                onSuccess(
                    this.$metadataService.DependentModuleValue(
                        this.propertyMetadata.LookupProperty,
                        this.parent,
                        this.metadataModel
                    )
                )
            );
        }
        if (clearCache)
        {
            this.$cachedCodeLookupService.clearCache(lookupType, filterString);
        }
        /*
         * This needs to be an explicit check since filterString can be an empty string and that is a valid value.
         * null and undefined however MUST return the $q deferred
         */
        return Utilities.IsNullOrUndefined(filterString)
            ? Promise.resolve([])
            : this.$cachedCodeLookupService.Get(lookupType, filterString)
                .then(onSuccess, onError);
        function onSuccess(result: KeyValuePair<MetadataKey, string>[]): Array<KeyValuePair<MetadataKey, string>>
        {
            const distinctResults =
                result && result.filter
                    ? result.filter((x, i, s) =>
                    {
                        return s.indexOf(x) === i;
                    })
                    : result;
            self._options = self.$metadataInputSelectService.MergeSelectOptions(
                lookupType,
                distinctResults,
                self.propertyMetadata,
                self.metadataModel
            );
            self.lookupSuccess();
            self.clearDeselectList();
            return result;
        }
        function onError(): Array<any>
        {
            return [];
        }
    }

    private createOdataFilter(): string
    {
        return this.$metadataPropertyService.createOdataFilter(this.propertyMetadata, this.metadataModel, this.parent);
    }

    fireValueChanged($event: any): void
    {
        if (this.showSpinner)
        {
            $event.preventDefault();
            $event.stopPropagation();
            return;
        }

        // REVIEW - If a Select field isn't required, should we insert an NONE option into the control?
        if (this.isRequired && Utilities.IsNullOrUndefined($event))
        {
            return;
        }
        const areSame = this.areAllEqual(<MetadataKey[]>$event, <MetadataKey[]>this.model);
        if (areSame || !this.onValueChanged)
        {
            return;
        }
        this.onValueChanged.emit({ value: $event });
    }

    selectAll(): void
    {
        if (this.optionSearch.length > 0)
        {
            this.selected = (this.selected as MetadataKey[]).concat(
                this._options
                    .filter(x => x.Value.toLowerCase().includes(this.optionSearch.toLowerCase()))
                    .map(x => x.Key)
                    .filter(x => !(this.selected as MetadataKey[]).includes(x))
            );
        }
        else
        {
            this.selected = this._options.map(x => x.Key);
        }

        this.addSelections();
    }

    deselectAll(): void
    {
        if (this.optionSearch.length > 0)
        {
            const self = this;
            const filteredItems = this._options
                .filter(x => x.Value.toLowerCase().includes(this.optionSearch.toLowerCase()))
                .map(x => x.Key);

            filteredItems.forEach(x =>
            {
                const i = (self.selected as MetadataKey[]).indexOf(x);
                if (i >= 0)
                {
                    (self.selected as MetadataKey[]).splice(i, 1);
                }
            });
        }
        else
        {
            this.selected = [];
        }
        this.fireValueChangedOnDeselect();
    }

    loadMore(): void
    {
        this.loadMoreCount++;
    }

    abstract shouldLoadMore(): boolean;

    get showLoadMore(): boolean
    {
        if (Utilities.IsNullOrUndefined(this._options))
            return false;
        else
            return this.shouldLoadMore();
    }

    get selectList(): KeyValuePair<MetadataKey, string>[]
    {
        if (!this._options)
            return [];
        return this.getSelectList();
    }

    clearOptionSearch(): void
    {
        this.optionSearch = "";
    }

    private areAllEqual(a: MetadataKey[], b: MetadataKey[]): boolean
    {
        if (!a && !b)
        {
            return true;
        }
        if (!a || !b)
        {
            return false;
        }
        return a.length === b.length && a.every((x, i) => String(x) === String(b[i]));
    }

    onKeyDown(event: KeyboardEvent): void
    {
        if (event.code === "NumpadAdd" && this.$securityService.UseTenKeyPlusAsTab)
        {
            event.preventDefault();
            this.$keyHandlerService.selectNextTabbableElement();
        }
    }

    initializeListeners(): void { return; }

    addSelections(): void { return; }

    lookupSuccess(): void { return; }

    fireValueChangedOnDeselect(): void { return; }

    clearDeselectList(): void { return; }

    abstract selectItem(item: MetadataKey): void;

    abstract finalizeSelect(): void;

    abstract setSelected(value: MetadataKey | MetadataKey[]): void;

    abstract applySort(unsorted: KeyValuePair<MetadataKey, string>[]): KeyValuePair<MetadataKey, string>[];

    abstract getSelectList(): KeyValuePair<MetadataKey, string>[];
}
